<template>
  <Dialog
    :visible="props.visible"
    class="dialog"
    :closable="true"
    :modal="true"
    :aria-label="' Edit ' + props.itemType + ' title'"
    :aria-expanded="visible"
    :close-on-escape="true"
  >
    <div
      v-focustrap aria-modal="true" class="wrapper"
    >
      <div
        class="close-wrapper"
      >
        <button
          aria-label="Close menu"
          class="close"
          :onclick="close"
          @keydown="($event) => {
            if ($event.key === 'Enter') {
              $event.preventDefault();
              close();
            }
          }"
        >
          <img
            role="presentation"
            :src="CloseGreyIcon"
            alt="Close menu"
          />
        </button>
      </div>


      <div
        class="title"
      >
        Edit {{ props.itemType }} title
      </div>
      <div
        class="toast"
        v-if="showToast"
      >
        <div
          class="toast-bar"
        />
        <img
          :src="InfoAlertIcon"
          alt="Alert"
          :style="{width: '2.4rem', height: '2.4rem', margin: '0'}"
        />
        <div
          class="toast-text"
        >
          Oops! Something went wrong. Please try again later.
        </div>
        <button
          aria-label="Close menu"
          class="toast-icon"
          :onclick="setShowToast(false)"
          @keydown="($event) => {
            if ($event.key === 'Enter') {
              $event.preventDefault();
              setShowToast(false);
            }
          }"
        >
          <img
            role="presentation"
            :src="CloseIcon"
            alt="Close menu"
          />
        </button>
      </div>


      <div class="input-wrapper">
        <span class="p-input-icon-right">
          <InputText
            :aria-label="'Edit title'"
            autofocus="true"
            id="newTitle"
            v-model="newTitle"
            class="input"
            @keydown="filterKey"
            @focus="showClear = true"
            @blur="delayRemove"
          />
          <img
            v-if="newTitle"
            @click="newTitle = ''"
            @keydown="($event) => {
              if ($event.key === 'Enter') {
                $event.preventDefault();
                newTitle = '';
              }
            }"
            aria-label="Clear input"
            :src=CloseCircle
            alt="Clear input"
            class="clear-icon"
          />
        </span>
        <div class="char-count">
          {{ newTitle.length }} / 50
        </div>
      </div>

      <div
        class="buttons"
        :style="{flexDirection: 'row', marginBottom: '4rem'}"
      >
        <Button
          aria-label="Cancel"
          @keydown="($event) => {
            if ($event.key === 'Enter') {
              $event.preventDefault();
              close();
            }
          }"
          @click="close"
          class="buttons-button buttons-cancel"
        >
          Cancel
        </button>
        <Button
          aria-label="Save"
          @keydown="($event) => {
            if ($event.key === 'Enter') {
              $event.preventDefault();
              changeTitle();
            }
          }"
          @click="changeTitle"
          class="buttons-button buttons-save"
        >
          Save
        </button>
      </div> 
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import {
  ref,
  onUpdated,
  defineEmits,
} from "vue";
import CloseGreyIcon from "../assets/images/Close-grey.svg";
import CloseCircle from "../assets/images/CloseCircle.svg";
import CloseIcon from "../assets/images/Close.svg";
import InfoAlertIcon from "../assets/images/InfoAlertRed.svg";
import { limitCharacter } from "../assets/helpers";
import store from "@/store";
import { SessionWithOrdersAndType } from "@/types/session-interfaces";
import { FolderWithSessions } from "@/types/folder-interfaces";

const props = defineProps({
  itemTitle: {
    type: String,
    required: true,
  },
  itemId: {
    type: String,
    required: true,
  },
  visible: {
    type: Boolean,
    required: true,
  },
  setVisible: {
    type: Function,
    required: true,
  },
  itemType: {
    type: String,
    default: 'session',
  },
});

const emit = defineEmits(["updateTitleSuccessful"]);

const newTitle = ref(props.itemTitle),
      loading = ref(false),
      showClear = ref(false),
      showToast = ref(false);

interface FilterKeyEvent extends KeyboardEvent {
  target: HTMLInputElement;
}

function filterKey(event: FilterKeyEvent) {
  limitCharacter(event, event.target.value);
}

async function changeTitle() {
  newTitle.value = newTitle.value.trim();
  if (newTitle.value === props.itemTitle || newTitle.value === "" ) {
    props.setVisible(false);
    return;
  }

  loading.value = true;

  const updateData: Partial<SessionWithOrdersAndType | FolderWithSessions> = {
    title: newTitle.value,
  };

  const payload = {
    id: props.itemId,
    updates: updateData
  }

  await store.dispatch('itemList/updateItem', payload)
    .then(() => {
      props.setVisible(false);
      showClear.value = false;
      loading.value = false;
      showToast.value = false;
      store.dispatch('itemList/clearSelection');
      emit("updateTitleSuccessful", newTitle.value);
    })
    .catch(() => {
      showToast.value = true;
      loading.value = false;
    });
}

function close() {
  props.setVisible(false);
}

function delayRemove() {
  // TODO: come up with something better
  setTimeout( () => showClear.value = false, 250)
}

function setShowToast(visible: boolean) {
  showToast.value = visible;
}

onUpdated (() => {
  newTitle.value = props.itemTitle;
})
</script>

<style lang="scss" scoped>
.dialog {
  .p-dialog-header {
    display: none;
  }
  
  .p-dialog-content {
    background-color: map-get($colours, accent-purple-2);
    width: 46rem;
    border-radius: 1.4rem;
    margin: 0;
    padding: 0;
  }

  .wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
    width: 46rem;
    margin: 0;
    padding: 0;
    background-color: map-get($colours, accent-purple-2);
    font-family: $font-secondary;

    .close-wrapper {
      display: flex;
      flex-direction: row-reverse;
      width: 44rem;
      margin: 0;

      .close {
        background-color: map-get($colours, accent-purple-2);
        margin-top: 1.6rem;
        margin-right: 1.6rem;
        width: 2.4rem;
        height: 2.4rem;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        img {
          width: 2.4rem;
          height: 2.4rem;
          margin: 0;
        }
      }
    }

    .title {
      @include h3-accent;
      margin: 0;
      padding: 0;
      margin-bottom: 2.4rem;
    }
    .input-wrapper {
      position: relative;
      width: 100%;
      margin: 0;
      padding: 0;
      margin-bottom: 2rem;

      .input {
        width: 38.8rem;
        padding: 1rem 3.2rem 1rem 1.2rem;
        color: white;
        background-color: map-get($colours, accent-purple-2);
        &:focus {
          background-color: map-get($colours, accent-purple-2);
          border: solid 0.1rem map-get($colours, grey-2);
        }

        &:hover {
          border: solid 0.1rem map-get($colours, grey-2);
        }
      }
      .clear-icon {
        position: absolute;
        top: 1.4rem;
        right: 4.2rem;
        width: 1.6rem;
        height: 1.6rem;
        cursor: pointer;
        pointer-events: auto;
        z-index: 2;
        filter: invert(1);
      }
      .char-count{
        text-align: right;
        font-size: 1.2rem;
        color: map-get($colours, grey-3);
        margin-top: 0.4rem;
        padding-right: 3.6rem;
        letter-spacing: 0.015rem;
      }
    }
    .buttons {
      display: flex;
      flex-direction: row;
      margin-bottom: 4rem;
      margin-top: 2rem;
      .buttons-button {
        width: 16.8rem;
        height: 5.6rem;
        margin: 0 2.4rem;
        border-radius: 0.8rem;
      }
      .buttons-save {
        color: map-get($colours, grey-7);
        background-color: map-get($colours, vibrant-purple);
        border: none;
      }
      .buttons-cancel {
        color: #474747;
        background-color: #d1d1d1;
      }
    }
  }
}
.clear-icon-wrapper {
  width: 2rem;
  height: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0;
  .clear-icon {
    width: 1.6rem;
    height: 1.6rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    margin: 0;
    margin-top: 0.5rem;
    img {
      margin: 0;
      width: 1.6rem;
      height: 1.6rem;
      margin-top: 1.2rem;
    }
    &:hover {
      background-color: transparent;
    }
  }
}

.toast {
  margin: 0;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.6rem;
  height: 5.6rem;
  border-radius: 0.8rem;
  width: 38.8rem;
  background-color: #f5dee2;
  padding: 0;
  .toast-bar {
    background-color: #b00020;
    width: 1rem;
    height: 100%;
    border-top-left-radius: 0.8rem;
    border-bottom-left-radius: 0.8rem;
  }
  .toast-text {
    font-size: 1.4rem;
    text-overflow: hidden;
    width: 27.2rem;
    color: #b00020;
    text-align: justify;
  }
  .toast-icon {
    width: 2.4rem;
    height: 2.4rem;
    background-color: transparent;
    margin: 0;
    margin-right: 1.2rem;
    img {
      width: 1.6rem;
      height: 1.6rem;
    }
  }
  img {
    margin: 0;
    width: 2.4rem;
    height: 2.4rem;
  }
}
</style>
